// Data extracted from Rosevilla Group Website

export const companyInfo = {
  name: "Rosevilla Group",
  tagline: "Creative. Responsible. Sustainable",
  subtitle: "Empowering financial future with expert investment management solutions.",
  pillars: [
    {
      title: "Creative",
      description: "Innovative deal structuring and strategic capital deployment that identifies unique opportunities in emerging markets."
    },
    {
      title: "Responsible",
      description: "Rigorous due diligence, transparent governance, and ethical investment practices that prioritize long-term value creation."
    },
    {
      title: "Sustainable",
      description: "Environmental stewardship and social impact at the core of every investment decision, building a better future."
    }
  ]
};

export const aboutContent = {
  title: "About Us",
  description: "Rosevilla Group is a premier investment holding company with a sophisticated approach to alternative investments across global markets. We identify high-potential opportunities in emerging sectors and leverage our extensive network to structure innovative investment solutions that deliver sustainable value.",
  whyChooseUs: [
    {
      title: "Institutional Knowledge",
      description: "Deep understanding of global financial markets, regulatory environments, and investment structures developed through extensive industry experience."
    },
    {
      title: "Strategic Networks",
      description: "Access to exclusive deal flow and co-investment opportunities through established relationships with leading institutional investors."
    },
    {
      title: "Sophisticated Structuring",
      description: "Creative financial engineering and deal structuring capabilities that optimize risk-adjusted returns across complex transactions."
    },
    {
      title: "Active Stewardship",
      description: "Hands-on approach to investment management ensuring alignment of interests and optimal outcomes for all stakeholders."
    }
  ],
  highlights: [
    "Alternative investment expertise",
    "Global market presence",
    "Institutional-grade processes",
    "Strategic value creation",
    "Long-term partnerships",
    "Sustainable growth focus"
  ]
};

export const services = [
  {
    id: 1,
    title: "Credit Investment",
    description: "We maintain strong relationships with leading non-banking financial institutions and credit fund managers in key markets. By identifying exclusive off-market opportunities, we enable our investors to collaborate with industry leaders and strategically deploy capital for optimal returns. Our approach is built on rigorous due diligence and creative structuring, ensuring sustainable value creation.",
    image: "https://images.unsplash.com/photo-1517196084897-498e0abd7c2d?crop=entropy&cs=srgb&fm=jpg&q=85"
  },
  {
    id: 2,
    title: "Healthcare Infrastructure Investment",
    description: "We collaborate with Australia's foremost healthcare service providers and care organizations, gaining deep insights into real participant needs. Through our expertise, investors gain access to high-demand and emerging healthcare investment opportunities, ensuring stable cash flow and returns that exceed market benchmarks. Our healthcare investments focus on creating innovative solutions that enhance service delivery and improve overall patient well-being.",
    image: "https://images.unsplash.com/photo-1738767226607-3853b6f890f8?crop=entropy&cs=srgb&fm=jpg&q=85"
  },
  {
    id: 3,
    title: "Renewable Energy Investments",
    description: "Partnering with global and Australian renewable energy manufacturers, operators, and technical advisors, we offer a comprehensive investment solution. From projects spanning a few megawatts to multi-gigawatt developments, and from early-stage approvals to full operational status, our experienced local team manages investments efficiently in the Oceania and Indian markets to maximize returns. Our goal is to drive the transition to a cleaner energy future through responsible and scalable renewable energy projects.",
    image: "https://images.unsplash.com/photo-1611177994968-d76b2bfda1e9?crop=entropy&cs=srgb&fm=jpg&q=85"
  },
  {
    id: 4,
    title: "Land Banking & Farmland Investments",
    description: "We identify and acquire high-quality agricultural land across strategic regions, leveraging our expertise in farmland valuation and agricultural economics. Our farmland investments focus on sustainable farming practices, ensuring long-term value appreciation while generating stable income through agricultural operations. We also specialize in land banking—strategic long-term holding of farmland for future rezoning and development opportunities. By identifying land with high growth potential in expanding urban corridors, we position our investors to benefit from significant capital appreciation when properties are rezoned for residential, commercial, or mixed-use development.",
    image: "https://images.unsplash.com/photo-1747195604489-bd8fa8a9c6e4?crop=entropy&cs=srgb&fm=jpg&q=85"
  },
  {
    id: 5,
    title: "Real Estate Investment",
    description: "Our real estate investment strategy encompasses commercial, residential, and mixed-use properties across prime locations in India, Asia, and Australia. We leverage our deep market knowledge and extensive network to identify undervalued assets with significant upside potential. Through active asset management, strategic repositioning, and value-add initiatives, we deliver superior risk-adjusted returns for our investors.",
    image: "https://images.unsplash.com/photo-1764955210511-64fa119fe0ef?crop=entropy&cs=srgb&fm=jpg&q=85"
  },
  {
    id: 6,
    title: "Private Equity Deals",
    description: "Access to the world's most sought-after private equity opportunities across category-defining companies. Our extensive institutional network provides exclusive allocation to late-stage funding rounds in breakthrough technology companies including SpaceX, Stripe, Epic Games, Databricks, and other unicorns reshaping global industries. We source proprietary deal flow across established businesses seeking growth capital, management buyouts, and operational transformations in manufacturing, consumer goods, financial services, and infrastructure. Our team's relationships with leading global institutions unlock investment opportunities typically reserved for the most sophisticated institutional investors.",
    image: "https://images.unsplash.com/photo-1776447242317-ec0ca782a043?crop=entropy&cs=srgb&fm=jpg&q=85"
  },
  {
    id: 7,
    title: "Venture Capital Deals",
    description: "Exclusive access to transformative venture capital opportunities in the world's most innovative companies. Our institutional relationships provide allocation to highly sought-after investment rounds in companies like Anthropic (Claude AI), xAI (Elon Musk's AI venture), Neuralink, OpenAI, and other pioneering firms at the forefront of artificial intelligence, biotechnology, and quantum computing. We identify and invest in high-growth technology-enabled startups across fintech, healthtech, cleantech, and enterprise software with scalable business models. Beyond capital, we provide strategic mentorship and access to our extensive network of industry leaders, helping portfolio companies achieve exponential growth and successful exits.",
    image: "https://images.unsplash.com/photo-1775797231060-431c478767ce?crop=entropy&cs=srgb&fm=jpg&q=85"
  }
];

export const storyContent = {
  title: "Our Story",
  subtitle: "Who We Are",
  description: "Rosevilla Group brings together a distinguished team of investment professionals with extensive backgrounds across premier global institutions. Our collective experience spans leading investment firms, asset management companies, global banking institutions, and top-tier advisory practices.",
  teamBackground: "Our team of professionals has held senior positions at some of the world's most respected financial institutions across investment banking, asset management, private equity, and advisory practices, providing us with deep expertise across multiple asset classes, geographies, and investment strategies.",
  institutions: [
    "Oaktree Capital Management",
    "Goldman Sachs",
    "Blackstone",
    "Deutsche Bank",
    "Deutsche Asset Management",
    "Credit Suisse",
    "Macquarie Group",
    "MUFG (Mitsubishi UFJ Financial Group)",
    "PAG",
    "Gaw Capital Partners",
    "Townsend Group",
    "Citigroup",
    "Standard Chartered",
    "KPMG"
  ],
  expertise: [
    "Alternative Investments",
    "Private Equity",
    "Real Assets",
    "Structured Finance",
    "Cross-Border Transactions",
    "Portfolio Management",
    "Legal & Regulatory",
    "Strategic Advisory"
  ],
  approach: "We leverage our institutional knowledge and extensive networks to identify compelling investment opportunities that others might overlook. Our experience with complex transactions and diverse market conditions enables us to navigate challenges and create value across economic cycles."
};

export const regions = [
  { name: "India", code: "IN" },
  { name: "Asia", code: "ASIA" },
  { name: "Australia", code: "AU" }
];

export const contactInfo = {
  email: "info@rosevillagroup.com",
  address: "402b/20 Lexington Dr, Bella Vista NSW 2153",
  locations: ["India", "Asia", "Australia"]
};

export const socialLinks = {
  linkedin: "https://www.linkedin.com/company/rosevilla-group/"
};

export const navLinks = [
  { name: "Home", href: "#home" },
  { name: "About", href: "#about" },
  { name: "Services", href: "#services" },
  { name: "Our Story", href: "#story" },
  { name: "Contact", href: "#contact" }
];

// Australian hero images
export const heroImages = {
  main: "https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?w=1920&h=1080&fit=crop",
  sydney: "https://images.unsplash.com/photo-1467803738586-46b7eb7b16a1?w=1920&h=1080&fit=crop",
  melbourne: "https://images.unsplash.com/photo-1531046155143-59b985b6ff8b?w=1920&h=1080&fit=crop"
};

// About section image (Australian cityscape)
export const aboutImage = "https://images.unsplash.com/photo-1561488111-5d800fd56b8a?w=800&h=600&fit=crop";
